/**
 * RBAC-UI matrix API (US-AE-IMP · @UC9002):
 *   GET /admin/rbac-ui-matrix        → AdminRbacUiMatrix (mọi vai đọc — render nav)
 *   PUT /admin/rbac-ui-matrix {body} → AdminRbacUiMatrix (CHỈ SUPER_ADMIN; 403 vai khác)
 *
 * Matrix CHỈ điều khiển UI (ẩn/hiện nav + enable nút). Server @Roles guard vẫn là chốt thật.
 * Raw fetch + Bearer (mirror features/users/api.ts). Validate response bằng Zod (C3).
 */
import { adminRbacUiMatrixSchema, type AdminRbacUiMatrix } from "@goppy/contracts";
import { env } from "@/lib/env.js";
import { getToken } from "@/lib/auth.js";

class RbacMatrixApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "RbacMatrixApiError";
    this.code = code;
    this.status = status;
  }
}

interface ProblemDetail {
  code?: string;
  detail?: string;
  title?: string;
}

async function toApiError(res: Response): Promise<RbacMatrixApiError> {
  let problem: ProblemDetail = {};
  try {
    problem = (await res.json()) as ProblemDetail;
  } catch {
    // body rỗng / không JSON.
  }
  const code = problem.code ?? `HTTP_${res.status}`;
  const message =
    res.status === 403
      ? "Bạn không có quyền chỉnh ma trận phân quyền (chỉ Super Admin)."
      : res.status === 401
        ? "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại."
        : res.status >= 500
          ? "Hệ thống đang gặp sự cố. Vui lòng thử lại sau."
          : (problem.detail ?? problem.title ?? "Không thể xử lý yêu cầu.");
  return new RbacMatrixApiError(code, message, res.status);
}

function authHeaders(): HeadersInit {
  const token = getToken();
  return {
    "content-type": "application/json",
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  };
}

/** GET matrix hiệu lực. */
export async function fetchRbacMatrix(): Promise<AdminRbacUiMatrix> {
  const res = await fetch(`${env.apiBaseUrl}/admin/rbac-ui-matrix`, {
    method: "GET",
    credentials: "include",
    headers: authHeaders(),
  });
  if (!res.ok) throw await toApiError(res);
  return adminRbacUiMatrixSchema.parse(await res.json());
}

/** PUT matrix mới (SUPER_ADMIN). */
export async function saveRbacMatrix(matrix: AdminRbacUiMatrix): Promise<AdminRbacUiMatrix> {
  const res = await fetch(`${env.apiBaseUrl}/admin/rbac-ui-matrix`, {
    method: "PUT",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify(matrix),
  });
  if (!res.ok) throw await toApiError(res);
  return adminRbacUiMatrixSchema.parse(await res.json());
}
